/**
 * Sworn Translator comparison cluster — /sworn/compare/{slug}.
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-03)
 * - "นักแปลสาบานตน" สำหรับเอกสารไทย = ผู้แปลที่สถานทูตปลายทางยอมรับ/ขึ้นบัญชี
 *   ไม่ใช่ตำแหน่งตามกฎหมายไทย
 * - นักแปลขึ้นทะเบียนกระทรวงยุติธรรมรับรอง "ความถูกต้องของคำแปล"
 * - ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารขึ้นทะเบียนกับสภาทนายความ
 * - นิติกรณ์: กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
 *   200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th
 * - ไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผลกับไทย 28 ก.พ. 2027
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */

export interface SwornCompareRow {
  aspect: string;
  a: string;
  b: string;
}

export interface SwornCompare {
  slug: string;
  title: string;
  description: string;
  h1: string;
  aLabel: string;
  bLabel: string;
  answer: string;
  rows: SwornCompareRow[];
  chooseA: string[];
  chooseB: string[];
  faqs: Array<{ q: string; a: string }>;
  synonyms: string[];
}

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — เงื่อนไขของสถานทูตและหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

export const SWORN_COMPARE: SwornCompare[] = [
  {
    slug: "sworn-vs-moj-translator",
    title: "นักแปลสาบานตนสถานทูต กับ นักแปลขึ้นทะเบียนกระทรวงยุติธรรม",
    description:
      "เทียบทีละข้อว่าเอกสารของคุณควรใช้คำแปลที่สถานทูตปลายทางยอมรับ หรือคำแปลจากนักแปลขึ้นทะเบียนกระทรวงยุติธรรม พร้อมลำดับขั้นนิติกรณ์ที่ต้องทำต่อ",
    h1: "นักแปลสาบานตนสถานทูต vs นักแปลขึ้นทะเบียนกระทรวงยุติธรรม",
    aLabel: "นักแปลที่สถานทูตยอมรับ",
    bLabel: "นักแปลขึ้นทะเบียน MoJ",
    answer:
      "ตัวชี้ขาดคือปลายทางของเอกสาร ถ้าเอกสารจะไปยื่นสถานทูตต่างประเทศหรือหน่วยงานในยุโรป ให้ยึดรายชื่อและรูปแบบที่สถานทูตนั้นประกาศไว้ ถ้าเอกสารจะใช้กับศาลไทยหรือหน่วยงานราชการไทย คำแปลจากนักแปลขึ้นทะเบียนกระทรวงยุติธรรมมักเป็นรูปแบบที่ผู้ตรวจคุ้นเคย หลายเคสใช้ต่อกันเป็นสายงานเดียว",
    rows: [
      { aspect: "ใครกำหนดเงื่อนไข", a: "สถานทูตของประเทศปลายทางในกรุงเทพ", b: "กระทรวงยุติธรรมและระเบียบของหน่วยงานไทย" },
      { aspect: "ปลายทางที่เหมาะ", a: "วีซ่า ทะเบียนราษฎร และหน่วยงานในยุโรป", b: "ศาลไทย อำเภอ เขต และหน่วยงานราชการไทย" },
      { aspect: "รูปแบบคำแปล", a: "ตามแบบที่สถานทูตกำหนด เช่น เย็บติดต้นฉบับและประทับตราคร่อม", b: "ตามแบบที่หน่วยงานไทยรับ พร้อมคำรับรองของผู้แปล" },
      { aspect: "ขั้นถัดไปที่พบบ่อย", a: "นิติกรณ์กรมการกงสุล แล้วประทับตราสถานทูตถ้ากำหนด", b: "นิติกรณ์กรมการกงสุลเมื่อจะนำออกไปใช้ต่างประเทศ" },
      { aspect: "ค่าธรรมเนียมราชการที่เกี่ยวข้อง", a: "นิติกรณ์ 200 บาท/ตราประทับ ด่วน 400 บาท และค่าธรรมเนียมของสถานทูต", b: "นิติกรณ์ 200 บาท/ตราประทับ ด่วน 400 บาท เมื่อใช้ต่างประเทศ" },
      { aspect: "ต้องมาด้วยตนเองไหม", a: "ขั้นแปลไม่ต้อง ขั้นยื่นสถานทูตบางแห่งนัดตัวผู้ยื่น", b: "ไม่ต้อง เริ่มงานจากไฟล์สแกนได้" },
    ],
    chooseA: [
      "ปลายทางเป็นสถานทูตยุโรปที่ประกาศรายชื่อผู้แปลที่ยอมรับ",
      "ยื่นทะเบียนสมรส ทะเบียนราษฎร หรือขอถิ่นที่อยู่ในต่างประเทศ",
      "เช็กลิสต์ระบุคำว่า sworn translation หรือ traduction assermentée",
    ],
    chooseB: [
      "ยื่นศาลไทย อำเภอ เขต หรือหน่วยงานราชการไทย",
      "ต้องการคำแปลภาษาไทยของเอกสารต่างประเทศเพื่อใช้ในไทย",
      "เช็กลิสต์ระบุเพียงว่าต้องเป็นคำแปลที่รับรองความถูกต้อง",
    ],
    faqs: [
      { q: "ใช้คำแปลชุดเดียวยื่นได้หลายประเทศไหม", a: "ในทางปฏิบัติมักไม่ได้ เพราะแต่ละสถานทูตกำหนดภาษา รูปแบบการเย็บเล่ม และชั้นตราประทับต่างกัน วิธีที่ประหยัดเวลาคือแจ้งปลายทางทั้งหมดตั้งแต่ต้น เพื่อจัดชุดเอกสารและจำนวนตราประทับให้พอในรอบเดียว " + TAIL },
      { q: "ถ้าปลายทางไม่ระบุชัดว่าต้องการแบบไหน", a: "ให้ขอเช็กลิสต์เป็นลายลักษณ์อักษรจากผู้รับเอกสารก่อนเริ่มงาน แล้วเก็บอีเมลตอบกลับไว้ประกอบชุดเอกสาร เพราะเมื่อเกิดข้อโต้แย้งภายหลัง หลักฐานนี้ช่วยยืนยันว่ารูปแบบที่ทำเป็นไปตามที่ปลายทางแจ้ง " + TAIL },
      { q: "ทั้งสองแบบต้องผ่านกรมการกงสุลเหมือนกันไหม", a: "ถ้าเอกสารจะถูกใช้นอกประเทศไทย ส่วนใหญ่ต้องผ่านนิติกรณ์ของกองสัญชาติและนิติกรณ์ กรมการกงสุล ไม่ว่าคำแปลจะมาจากทางใด ค่าธรรมเนียมคือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน " + TAIL },
      { q: "ค่าบริการต่างกันมากไหม", a: "เว็บไซต์ไม่แสดงราคาบริการ เพราะขึ้นกับภาษา จำนวนหน้า ความซับซ้อนของเอกสาร และจำนวนชั้นรับรองที่ปลายทางกำหนด ส่งไฟล์สแกนมาทางโทรศัพท์ LINE หรืออีเมล เพื่อให้ทีมงานประเมินเป็นรายเคส " + TAIL },
    ],
    synonyms: ["sworn translator vs moj translator", "แปลสถานทูต ต่างกับ แปลกระทรวงยุติธรรม", "certified translation thailand which one"],
  },
  {
    slug: "sworn-vs-notary",
    title: "แปลสาบานตน กับ รับรองโดยทนาย Notary Public ต่างกันอย่างไร",
    description:
      "เทียบชั้นการรับรองระหว่างคำแปลที่สถานทูตยอมรับ กับการรับรองลายมือชื่อและสำเนาโดยทนายความผู้ทำคำรับรอง ว่าเมื่อไหร่ต้องใช้อย่างใดอย่างหนึ่ง และเมื่อไหร่ต้องใช้ทั้งคู่",
    h1: "แปลสาบานตน vs Notary Public: รับรองคนละสิ่ง",
    aLabel: "แปลสาบานตน",
    bLabel: "ทนายความผู้ทำคำรับรอง",
    answer:
      "สองอย่างนี้ไม่ใช่ทางเลือกแทนกัน คำแปลรับรองว่าข้อความตรงกับต้นฉบับ ส่วนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองว่าใครลงลายมือชื่อ หรือสำเนาตรงกับต้นฉบับ เอกสารที่ยื่นต่างประเทศจำนวนมากต้องใช้ทั้งสองอย่างต่อกัน แล้วจึงเข้าสู่ขั้นนิติกรณ์",
    rows: [
      { aspect: "รับรองอะไร", a: "ความถูกต้องของคำแปลเทียบต้นฉบับ", b: "ลายมือชื่อ สำเนาถูกต้อง และคำสาบาน" },
      { aspect: "ผู้รับรอง", a: "ผู้แปลที่ปลายทางยอมรับ", b: "ทนายความที่ได้รับอนุญาตจากสภาทนายความ" },
      { aspect: "ต้องแสดงตนไหม", a: "ไม่ต้อง ส่งไฟล์สแกนเริ่มงานได้", b: "ผู้ลงนามต้องแสดงตนต่อทนายความ" },
      { aspect: "เอกสารที่พบบ่อย", a: "สูติบัตร ทะเบียนบ้าน ใบรับรองโสด ทรานสคริปต์", b: "หนังสือมอบอำนาจ Affidavit สำเนาพาสปอร์ต" },
      { aspect: "ใช้ต่อกันอย่างไร", a: "เป็นฐานของชุดเอกสารก่อนขั้นรับรองอื่น", b: "ใช้เมื่อปลายทางต้องการยืนยันตัวผู้ลงนาม" },
      { aspect: "ขั้นถัดไป", a: "นิติกรณ์กรมการกงสุล แล้วสถานทูตปลายทางถ้ากำหนด", b: "นิติกรณ์กรมการกงสุล แล้วสถานทูตปลายทางถ้ากำหนด" },
    ],
    chooseA: [
      "เอกสารเป็นภาษาไทยและปลายทางอ่านภาษาอื่น",
      "เช็กลิสต์ระบุ certified หรือ sworn translation",
      "ยื่นวีซ่า ทะเบียนราษฎร หรือสมัครเรียนต่างประเทศ",
    ],
    chooseB: [
      "เช็กลิสต์ระบุ notarised signature หรือ certified true copy",
      "ต้องทำหนังสือมอบอำนาจหรือคำสาบานประกอบ",
      "ปลายทางต้องการยืนยันว่าผู้ลงนามคือบุคคลนั้นจริง",
    ],
    faqs: [
      { q: "ทำสองอย่างพร้อมกันได้ไหม", a: "ได้ และมักประหยัดเวลากว่าเพราะเอกสารเดินเป็นชุดเดียว แต่ต้องเรียงลำดับให้ถูก โดยทั่วไปจัดคำแปลให้เสร็จก่อน แล้วจึงให้ทนายความรับรองในส่วนที่ปลายทางกำหนด ก่อนนำทั้งชุดเข้าขั้นนิติกรณ์ " + TAIL },
      { q: "ทนายความรับรองคำแปลได้เองไหม", a: "ทนายความผู้ทำคำรับรองรับรองลายมือชื่อของผู้แปลหรือรับรองสำเนาได้ แต่นั่นเป็นคนละเรื่องกับการรับรองว่าคำแปลถูกต้อง ผู้ตรวจปลายทางจะอ่านว่าใครรับรองอะไร จึงควรระบุฐานะที่ใช้รับรองให้ชัดในเอกสาร " + TAIL },
      { q: "ถ้าปลายทางขอ Apostille ต้องทำอย่างไร", a: "ณ ส.ค. 2026 ประเทศไทยยังไม่ออก Apostille เพราะอนุสัญญาจะมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2027 ระหว่างนี้เอกสารไทยยังใช้เส้นทางนิติกรณ์ของกรมการกงสุลและตราสถานทูตปลายทาง ควรแจ้งปลายทางและขอคำยืนยันเป็นลายลักษณ์อักษร " + TAIL },
      { q: "ค่าใช้จ่ายส่วนไหนเป็นของราชการ", a: "ค่าธรรมเนียมนิติกรณ์ของกองสัญชาติและนิติกรณ์ กรมการกงสุล คือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน ส่วนค่าธรรมเนียมของสถานทูตแต่ละแห่งกำหนดเอง ค่าบริการงานแปลและงานเดินเอกสารสอบถามทางโทรศัพท์ LINE หรืออีเมล " + TAIL },
    ],
    synonyms: ["แปลสาบานตน ต่างกับ notary", "sworn translation vs notarisation", "ต้องโนตารีด้วยไหม"],
  },
  {
    slug: "sworn-vs-mfa-legalisation",
    title: "แปลสาบานตน กับ นิติกรณ์กรมการกงสุล คนละขั้นกัน",
    description:
      "อธิบายว่าคำแปลกับตราประทับนิติกรณ์ของกรมการกงสุลทำหน้าที่ต่างกันอย่างไร ลำดับที่ถูกต้องคืออะไร และค่าธรรมเนียมราชการเท่าไร",
    h1: "แปลสาบานตน vs นิติกรณ์กรมการกงสุล",
    aLabel: "คำแปลสาบานตน",
    bLabel: "นิติกรณ์กรมการกงสุล",
    answer:
      "คำแปลทำให้ผู้ตรวจต่างประเทศอ่านเอกสารไทยได้ ส่วนนิติกรณ์ของกองสัญชาติและนิติกรณ์ กรมการกงสุล เป็นการรับรองว่าลายมือชื่อและตราบนเอกสารเป็นของหน่วยงานไทยจริง สองอย่างนี้จึงทำต่อกัน ไม่ใช่เลือกอย่างใดอย่างหนึ่ง",
    rows: [
      { aspect: "หน้าที่", a: "แปลข้อความให้ตรงต้นฉบับ", b: "รับรองลายมือชื่อและตราของหน่วยงานผู้ออกเอกสาร" },
      { aspect: "ผู้ดำเนินการ", a: "ผู้แปลที่ปลายทางยอมรับ", b: "กองสัญชาติและนิติกรณ์ กรมการกงสุล" },
      { aspect: "ลำดับ", a: "ทำหลังได้ต้นฉบับราชการฉบับล่าสุด", b: "ทำหลังคำแปลเสร็จ พร้อมยื่นต้นฉบับควบคู่" },
      { aspect: "ค่าธรรมเนียม", a: "เป็นค่าบริการเอกชน สอบถามเป็นรายเคส", b: "200 บาทต่อตราประทับ ปกติ · 400 บาทต่อตราประทับ ด่วน" },
      { aspect: "ผลลัพธ์", a: "ชุดคำแปลพร้อมคำรับรองของผู้แปล", b: "ตราประทับรับรองบนเอกสารและคำแปล" },
      { aspect: "ขั้นถัดไป", a: "เข้าสู่ขั้นนิติกรณ์", b: "ประทับตราสถานทูตปลายทางถ้าสถานทูตกำหนด" },
    ],
    chooseA: [
      "ปลายทางอ่านภาษาไทยไม่ได้และขอคำแปล",
      "ต้องการเริ่มงานล่วงหน้าจากไฟล์สแกน",
      "ยังไม่ทราบว่าปลายทางต้องการตราประทับกี่ชั้น",
    ],
    chooseB: [
      "ปลายทางระบุว่าเอกสารต้องผ่านการรับรองจากกระทรวงการต่างประเทศ",
      "เอกสารจะถูกใช้นอกประเทศไทย",
      "สถานทูตปลายทางกำหนดให้ผ่านนิติกรณ์ก่อนจึงรับเรื่อง",
    ],
    faqs: [
      { q: "ข้ามขั้นนิติกรณ์ได้ไหมถ้าคำแปลดีอยู่แล้ว", a: "ไม่ได้ในกรณีที่ปลายทางระบุไว้ เพราะนิติกรณ์เป็นการยืนยันความแท้จริงของลายมือชื่อและตราหน่วยงานไทย ซึ่งคุณภาพของคำแปลไม่ได้ทดแทน ถ้าข้ามขั้นนี้ สถานทูตปลายทางมักส่งเรื่องกลับให้ไปดำเนินการก่อน " + TAIL },
      { q: "ต้องนับตราประทับกี่ดวง", a: "นับตามจำนวนหน้าหรือชุดเอกสารที่ต้องรับรองตามเกณฑ์ของหน่วยงาน โดยค่าธรรมเนียมคิดเป็นรายตราประทับที่ 200 บาทแบบปกติ และ 400 บาทแบบด่วน การเตรียมรายการเอกสารให้ครบตั้งแต่รอบแรกช่วยลดการเดินเรื่องซ้ำ " + TAIL },
      { q: "แบบด่วนเร็วกว่ากี่วัน", a: "แบบด่วนมีค่าธรรมเนียมสูงกว่าและรอบให้บริการสั้นกว่าแบบปกติ แต่ระยะเวลาจริงขึ้นกับปริมาณคิวของหน่วยงานในช่วงนั้น จึงควรวางแผนเผื่อเวลาเสมอ และอย่ายึดวันนัดยื่นวีซ่าเป็นเส้นตายเดียว " + TAIL },
      { q: "เอกสารที่ผ่านนิติกรณ์แล้วแก้ไขได้ไหม", a: "แก้ไม่ได้ ถ้าพบข้อผิดพลาดหลังประทับตรา ต้องแก้ที่คำแปลแล้วเข้าคิวรับรองใหม่ทั้งชุด นี่คือเหตุผลที่ควรตรวจการสะกดชื่อ วันเดือนปี และเลขที่เอกสารกับพาสปอร์ตให้ตรงก่อนยื่น " + TAIL },
    ],
    synonyms: ["นิติกรณ์ คืออะไร", "แปลแล้วต้องรับรองกงสุลไหม", "mfa legalisation thailand translation"],
  },
  {
    slug: "sworn-vs-agency-translation",
    title: "แปลสาบานตน กับ แปลทั่วไปจากร้านแปล ใช้แทนกันได้ไหม",
    description:
      "เทียบคำแปลที่จัดทำเพื่อผ่านการรับรองของสถานทูตและกรมการกงสุล กับคำแปลทั่วไปเพื่อความเข้าใจ ว่าต่างกันที่รูปแบบใด และเสี่ยงถูกตีกลับตรงไหน",
    h1: "แปลสาบานตน vs แปลทั่วไป: ต่างกันที่การถูกยอมรับ",
    aLabel: "คำแปลเพื่อการรับรอง",
    bLabel: "คำแปลทั่วไป",
    answer:
      "คำแปลทั่วไปเน้นให้อ่านเข้าใจ ส่วนคำแปลเพื่อการรับรองต้องจัดรูปแบบให้ผู้ตรวจเทียบกับต้นฉบับได้ทีละบรรทัด มีคำรับรองของผู้แปล ระบุตราและลายมือชื่อ และผ่านชั้นรับรองที่ปลายทางกำหนด ถ้าใช้คำแปลทั่วไปยื่นราชการ มักถูกส่งกลับ",
    rows: [
      { aspect: "เป้าหมาย", a: "ให้หน่วยงานยอมรับเป็นเอกสารประกอบคำร้อง", b: "ให้ผู้อ่านเข้าใจเนื้อหา" },
      { aspect: "รูปแบบ", a: "เรียงตามต้นฉบับ ระบุตรา ลายมือชื่อ และเลขที่เอกสาร", b: "จัดรูปแบบอิสระ" },
      { aspect: "คำรับรองผู้แปล", a: "มี พร้อมชื่อและข้อมูลติดต่อผู้แปล", b: "โดยทั่วไปไม่มี" },
      { aspect: "ขั้นรับรองต่อ", a: "เข้าสู่ขั้นนิติกรณ์และตราสถานทูตได้", b: "นำไปรับรองต่อไม่ได้" },
      { aspect: "ความเสี่ยงถูกตีกลับ", a: "ต่ำลงเมื่อทำตามเช็กลิสต์ปลายทาง", b: "สูงเมื่อใช้กับงานราชการ" },
      { aspect: "เหมาะกับ", a: "วีซ่า ทะเบียนราษฎร ศาล มหาวิทยาลัย", b: "อ่านภายใน สรุปเนื้อหา งานการตลาด" },
    ],
    chooseA: [
      "เอกสารต้องยื่นหน่วยงานราชการหรือสถานทูต",
      "ต้องนำคำแปลไปผ่านนิติกรณ์ต่อ",
      "ปลายทางกำหนดว่าต้องมีคำรับรองของผู้แปล",
    ],
    chooseB: [
      "ใช้อ่านทำความเข้าใจภายในองค์กร",
      "เอกสารไม่ต้องยื่นหน่วยงานใด",
      "ต้องการเนื้อหาโดยสรุปเพื่อประกอบการตัดสินใจ",
    ],
    faqs: [
      { q: "แปลเองแล้วให้ทนายรับรองพอไหม", a: "ขึ้นกับเช็กลิสต์ของปลายทาง บางหน่วยงานยอมรับคำแปลของผู้ยื่นเมื่อมีผู้รับรองลายมือชื่อ แต่สถานทูตหลายแห่งกำหนดผู้แปลที่ตนยอมรับเท่านั้น ควรยืนยันข้อกำหนดก่อนลงมือ เพื่อไม่ต้องแปลซ้ำ " + TAIL },
      { q: "ใช้เครื่องแปลอัตโนมัติแล้วให้คนตรวจได้ไหม", a: "เอกสารราชการมีถ้อยคำเฉพาะ เช่น ชื่อหน่วยงาน สถานะบุคคล และข้อความในตราประทับ ซึ่งเครื่องแปลมักแปลไม่ตรงรูปแบบที่ผู้ตรวจคุ้นเคย ผลคือเสี่ยงถูกตีกลับและเสียเวลามากกว่าที่ประหยัดได้ " + TAIL },
      { q: "คำแปลเก่าที่เคยใช้แล้วนำกลับมาใช้ได้ไหม", a: "ใช้ซ้ำได้ถ้าข้อมูลไม่เปลี่ยนและปลายทางไม่กำหนดอายุเอกสาร แต่เอกสารสถานะบุคคล เช่น ใบรับรองโสดหรือหนังสือรับรองความประพฤติ มักถูกกำหนดให้ออกใหม่ภายใน 3-6 เดือน จึงต้องคัดต้นฉบับใหม่และแปลใหม่ " + TAIL },
      { q: "ประเมินค่าบริการอย่างไร", a: "เว็บไซต์ไม่แสดงราคาบริการ ทีมงานประเมินจากภาษา จำนวนหน้า ความซับซ้อน และชั้นรับรองที่ปลายทางกำหนด ส่งไฟล์สแกนพร้อมเช็กลิสต์มาทางโทรศัพท์ LINE หรืออีเมล เพื่อรับการประเมินเป็นรายเคส " + TAIL },
    ],
    synonyms: ["แปลทั่วไปใช้ยื่นได้ไหม", "certified vs standard translation", "แปลเองยื่นสถานทูตได้ไหม"],
  },
];

export const SWORN_COMPARE_BY_SLUG: Record<string, SwornCompare> =
  Object.fromEntries(SWORN_COMPARE.map((c) => [c.slug, c]));
